// src/components/NavMenu.tsx
import { Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useUploadQueue } from "../contexts/UploadQueueContext";
import { MedewerkerType as Role } from "../enums";
import { ConfirmModal } from "./modal";
import { isDevOmgeving } from "../utils/omgeving";
import styles from "./NavMenu.module.css";

/* ----------------------------------------------------------------------------
 * Navigatie: items per groep, met toegang per rol. Groepen worden gescheiden
 * door een dun streepje (geen kopjes). Lege groepen (na rol-filter) vallen weg,
 * zodat er nooit een los of dubbel streepje verschijnt.
 *
 * Let op: dit menu regelt enkel client-side zichtbaarheid. De echte afscherming
 * zit in RoleGuard (App.tsx) en de backend-guards.
 * -------------------------------------------------------------------------- */
type NavItem = {
  label: string;
  to: string;
  roles: Role[];
  /** Alleen zichtbaar in de dev-omgeving (zie `utils/omgeving.ts`). */
  alleenDev?: boolean;
};
type NavGroep = NavItem[];

// Alle medewerkerssoorten — de persoonlijke items zijn voor iedereen zichtbaar.
const IEDEREEN: Role[] = [
  Role.ARTS,
  Role.ASSISTENT,
  Role.RADIOLOGIE_ASSISTENT,
  Role.WAARNEMEND_ASSISTENT,
  Role.VOORBEREIDINGS_ASSISTENT,
  Role.PM,
  Role.INPLANNER,
  Role.MANAGER,
  Role.FINANCIELE_MANAGER,
  Role.ADMIN,
];

const NAV_GROEPEN: NavGroep[] = [
  // Mijn werk (persoonlijk)
  [
    { label: "Mijn dashboard", to: "/dashboard", roles: IEDEREEN },
    {
      label: "Mijn afgeronde dossiers",
      to: "/afgeronde-dossiers",
      roles: IEDEREEN,
    },
    {
      // Q18 (fixronde oktober 2026): nog niet af; buiten DEV voor iedereen
      // onzichtbaar. De route (App.tsx) en de backend (DevOnlyGuard op
      // /me/finances) sluiten mee.
      label: "Mijn financiën",
      to: "/financien",
      roles: IEDEREEN,
      alleenDev: true,
    },
    { label: "Mijn instellingen", to: "/mijn-instellingen", roles: IEDEREEN },
  ],
  // Overzicht
  [
    {
      label: "Manager dashboard",
      to: "/manager-dashboard",
      roles: [Role.PM, Role.MANAGER, Role.FINANCIELE_MANAGER, Role.ADMIN],
    },
    {
      // Besluit #1 basisdocument financieel dashboard: alléén MANAGER —
      // bewust géén ADMIN of FINANCIELE_MANAGER (harde regel).
      label: "Financieel dashboard",
      to: "/financieel-dashboard",
      roles: [Role.MANAGER],
    },
    {
      // Workbench voor typo-datums (ontwerpdoc 2026-07-24); sinds D28
      // (overleg Martine 02-09-2026) alléén ADMIN — dat is alleen Niels.
      label: "Datakwaliteit",
      to: "/datakwaliteit",
      roles: [Role.ADMIN],
    },
    {
      label: "Alle afgeronde dossiers",
      to: "/alle-afgeronde-dossiers",
      roles: [Role.PM, Role.MANAGER, Role.ADMIN],
    },
    // Locatie-overzicht: uit het menu sinds D30 (overleg Martine 02-09-2026);
    // de pagina en het endpoint (nu ADMIN-only) blijven bestaan.
  ],
  // Dossiers & planning
  [
    {
      label: "Nieuw dossier",
      to: "/dossiers/new",
      roles: [
        Role.PM,
        Role.INPLANNER,
        Role.MANAGER,
        Role.FINANCIELE_MANAGER,
        Role.ADMIN,
      ],
    },
    // K4: Inplannen alleen voor Inplanner, Admin en PM (Manager eraf).
    {
      label: "Inplannen",
      to: "/inplannen",
      roles: [Role.INPLANNER, Role.ADMIN, Role.PM],
    },
    // A14: dossiers die nog een voorbereidingsassistent zoeken.
    {
      label: "Voorbereidingen",
      to: "/voorbereidingen",
      roles: [Role.PM, Role.MANAGER, Role.ADMIN, Role.VOORBEREIDINGS_ASSISTENT],
    },
    // Feedback kantoor 1-11-2026: het planningsbestand van de assistenten.
    {
      label: "Planning assistenten",
      to: "/planning-assistenten",
      roles: [
        Role.ASSISTENT,
        Role.RADIOLOGIE_ASSISTENT,
        Role.WAARNEMEND_ASSISTENT,
        Role.VOORBEREIDINGS_ASSISTENT,
        Role.PM,
        Role.MANAGER,
        Role.ADMIN,
      ],
    },
  ],
  // Beheer
  [
    {
      label: "Medewerkers",
      to: "/medewerker",
      roles: [Role.MANAGER, Role.ADMIN],
    },
    {
      label: "Producten",
      to: "/product",
      roles: [Role.MANAGER, Role.FINANCIELE_MANAGER, Role.ADMIN],
    },
    {
      label: "Facturen",
      to: "/facturen",
      roles: [Role.MANAGER, Role.FINANCIELE_MANAGER, Role.ADMIN],
    },
    {
      label: "Aanvullende facturen",
      to: "/aanvullende-facturen",
      roles: [Role.MANAGER, Role.ADMIN],
    },
    {
      label: "Salarisadministratie",
      to: "/salaris",
      roles: [Role.MANAGER, Role.ADMIN, Role.FINANCIELE_MANAGER],
    },
    {
      label: "Beheer standaardwaarden",
      to: "/beheer/standaardwaarden",
      roles: [Role.MANAGER, Role.ADMIN],
    },
    {
      // I17 (besluit Niels 07-10-2026): nachtelijke controle van de
      // SharePoint-bibliotheek, alleen MANAGER en ADMIN.
      label: "Bestandscontrole",
      to: "/bestandscontrole",
      roles: [Role.MANAGER, Role.ADMIN],
    },
    {
      // WP6 (besluit Niels 12-10-2026): activiteitenlog en onboarding,
      // alleen MANAGER en ADMIN.
      label: "Activiteitenlog",
      to: "/activiteit",
      roles: [Role.MANAGER, Role.ADMIN],
    },
    {
      // Besluit W5 webinars-basisdocument, aangescherpt in D29 (overleg
      // Martine 02-09-2026): ADMIN ∪ WEBINAR_MANAGER — MANAGER eraf; bewust
      // NIET in IEDEREEN — een account met alleen WEBINAR_MANAGER (Janneke)
      // ziet zo precies één menu-item.
      label: "Webinars",
      to: "/webinars",
      roles: [Role.ADMIN, Role.WEBINAR_MANAGER],
    },
    {
      label: "Prijsbeheer (historie)",
      to: "/prijsbeheer",
      roles: [Role.ADMIN],
    },
  ],
  // Kennis
  [{ label: "Kennisbank", to: "/kennisbank", roles: IEDEREEN }],
  // Systeem (alleen admin) — dev-/migratie-items
  [
    { label: "Instellingen", to: "/admin", roles: [Role.ADMIN] },
    { label: "Migratie Workbench", to: "/migration", roles: [Role.ADMIN] },
    {
      label: "Factuurmigratie (Dropbox)",
      to: "/migration/invoices",
      roles: [Role.ADMIN],
    },
    {
      label: "Financial compare (STG)",
      to: "/migration/financial-compare",
      roles: [Role.ADMIN],
    },
  ],
];

export default function NavMenu() {
  const { hasRole, logout } = useAuth();
  const { lopend, wachtrij } = useUploadQueue();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [uitloggenBevestigen, setUitloggenBevestigen] = useState(false);

  // Filter elke groep op rol (en dev-poort) en laat lege groepen weg.
  const zichtbareGroepen = NAV_GROEPEN.map((groep) =>
    groep.filter(
      (item) =>
        hasRole(...item.roles) && (!item.alleenDev || isDevOmgeving()),
    ),
  ).filter((groep) => groep.length > 0);

  const uitloggen = () => {
    setUitloggenBevestigen(false);
    logout();
    navigate("/login");
    setOpen(false);
  };

  // Uitloggen breekt een lopende upload af: eerst vragen.
  const handleLogout = () => {
    if (lopend.length > 0 || wachtrij.length > 0) {
      setUitloggenBevestigen(true);
      return;
    }
    uitloggen();
  };

  return (
    <>
      {/* hamburger-knop — opent én sluit het menu (toggle) */}
      <button
        className={`${styles.hamburger} ${open ? styles.hamburgerOpen : ""}`}
        aria-label={open ? "Menu sluiten" : "Menu openen"}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span />
        <span />
        <span />
      </button>

      {/* overlay */}
      {open && (
        <div className={styles.overlay} onClick={() => setOpen(false)} />
      )}

      {/* uitschuif-zijbalk */}
      <nav className={`${styles.sidebar} ${open ? styles.open : ""}`}>
        <div className={styles.items}>
          {zichtbareGroepen.map((groep, index) => (
            <Fragment key={groep[0].to}>
              {index > 0 && <hr className={styles.divider} />}
              {groep.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={styles.item}
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </Fragment>
          ))}
        </div>

        {/* Uitloggen — vastgepind onderin de balk */}
        <div className={styles.footer}>
          <button onClick={handleLogout} className={styles.logout}>
            Uitloggen
          </button>
        </div>
      </nav>

      {uitloggenBevestigen && (
        <ConfirmModal
          title="Er loopt nog een upload"
          description="Als je nu uitlogt, stopt de upload en worden de resterende bestanden niet geüpload. Wacht tot de upload klaar is, of log toch uit."
          confirmLabel="Toch uitloggen"
          cancelLabel="Wachten"
          onConfirm={uitloggen}
          onCancel={() => setUitloggenBevestigen(false)}
        />
      )}
    </>
  );
}
